import { Injectable } from '@angular/core';
import { EntityWithJobs, SimpleEntity } from '../../../modules/platform/sources/entities/models/simple-entity';
import { Observable, from, map, mergeMap, toArray } from 'rxjs';
import { TenantIngestionJobService } from 'src/app/core/services/application/tenant-ingestion-job.service';
import { SelectableJob } from 'src/app/shared/components/select-jobs-grid/select-job.models';
import { TenantSchedule } from '../../models/tenant-schedule';

export enum JobPreselectionMode {
    None,
    OneActivePerIngestMode,
    FirstActive,
    All
}
@Injectable({
    providedIn: 'root'
})
export class SelectJobsService {
    private static NumberConcurrentRequests = 4;
    public constructor(private ingestionJobService: TenantIngestionJobService) {}

    public getSelectableJobs(
        tenantId: string,
        sourceId: number,
        sourceName: string,
        entities: SimpleEntity[],
        jobPreselection: JobPreselectionMode = JobPreselectionMode.FirstActive
    ): Observable<SelectableJob[]> {
        return from(entities).pipe(
            mergeMap(
                entity =>
                    this.ingestionJobService
                        .getTenantIngestionJobsV2(tenantId, sourceId, entity.tenantSourceEntityId)
                        .pipe(map(ingestions => ({ ...entity, ingestions }))),
                SelectJobsService.NumberConcurrentRequests
            ),
            toArray(),
            map(entities => {
                entities.forEach(e => {
                    e.ingestions = e.ingestions.sort(
                        (a, b) => <number>a.tenantIngestionId - <number>b.tenantIngestionId
                    );
                });
                entities.sort((one, two) => one.tenantSourceEntityId - two.tenantSourceEntityId);

                const jobs = this.mapToViewModels(entities, sourceName);
                this.preselectJobs(jobs, jobPreselection);
                return jobs;
            })
        );
    }

    public mapToViewModels(
        entities: EntityWithJobs[],
        sourceName: string,
        schedules?: TenantSchedule[]
    ): SelectableJob[] {
        return entities
            .filter(e => e.ingestions.length > 0)
            .flatMap((entity, entityIndex) => {
                const entityId = entity.tenantSourceEntityId;
                const entityName = this.getFullEntityName(entity);
                let firstActiveJobHasBeenSetToSelected = false;
                return entity.ingestions.map(ingestion => {
                    const schedDesc =
                        ingestion.tenantScheduleDescription ||
                        (ingestion.tenantScheduleId &&
                            schedules &&
                            schedules?.find(s => s.tenantScheduleId === ingestion.tenantScheduleId)?.description) ||
                        'Not scheduled';
                    const selectableJob = {
                        activeFlag: ingestion.activeFlag,
                        isSelected: !!ingestion.activeFlag && !firstActiveJobHasBeenSetToSelected,
                        ingestionId: ingestion.tenantIngestionId,
                        ingestMode: ingestion.rawMode,
                        batchSize: ingestion.rawBatchSize,
                        watermarkKey: ingestion.rawWatermarkKey,
                        watermarkValue: ingestion.rawWatermarkValue,
                        backfillMinimum: ingestion.rawBackfillMinimum,
                        tenantScheduleId: ingestion.tenantScheduleId,
                        scheduleDescription: schedDesc,
                        hasSched: !!ingestion.tenantScheduleId,
                        rowGroupOrdinal: entityIndex,
                        entityId,
                        entityName: entityName || 'Unnamed Entity',
                        notes: ingestion.notes || '',
                        source: sourceName,
                        priority: ingestion.priority
                    };
                    if (ingestion.activeFlag) {
                        firstActiveJobHasBeenSetToSelected = true;
                    }
                    return selectableJob;
                });
            });
    }

    getJobsUnderScheduleIds(
        tenantId: string,
        scheduleIds: number[],
        ingestionIds: number[] = []
    ): Observable<SelectableJob[]> {
        let firstActiveJobHasBeenSetToSelected = false;
        return this.ingestionJobService.getTenantIngestionJobsV1(tenantId, scheduleIds, 'tenant_schedule_ids').pipe(
            map(jobs => {
                return jobs.map(ingestion => {
                    const selectableJob: SelectableJob = {
                        activeFlag: ingestion.activeFlag,
                        isSelected:
                            ingestionIds.includes(ingestion.tenantIngestionId || 0) ||
                            (!!ingestion.activeFlag && !firstActiveJobHasBeenSetToSelected && ingestionIds.length == 0),
                        ingestionId: ingestion.tenantIngestionId,
                        ingestMode: ingestion.rawMode,
                        batchSize: ingestion.rawBatchSize,
                        watermarkKey: ingestion.rawWatermarkKey,
                        watermarkValue: ingestion.rawWatermarkValue,
                        backfillMinimum: ingestion.rawBackfillMinimum,
                        tenantScheduleId: ingestion.tenantScheduleId,
                        scheduleDescription: ingestion.tenantScheduleDescription,
                        hasSched: !!ingestion.tenantScheduleId,
                        rowGroupOrdinal: 0,
                        entityId: ingestion.tenantSourceEntityId,
                        entityName: ingestion.entity || 'Unnamed Entity',
                        notes: ingestion.notes || '',
                        source: ingestion.tenantSourceDescription,
                        priority: ingestion.priority
                    };
                    if (ingestion.activeFlag) {
                        firstActiveJobHasBeenSetToSelected = true;
                    }
                    return selectableJob;
                });
            })
        );
    }

    public preselectJobs(jobs: SelectableJob[], jobPreselection: JobPreselectionMode): void {
        const jobsByEntityId = this.getJobsByEntityId(jobs);

        switch (jobPreselection) {
            case JobPreselectionMode.OneActivePerIngestMode:
                jobsByEntityId.forEach(jobGroup => {
                    const selectedIngestModes = new Array<string | null>();
                    // sorting so we select the first active job, if there is an active job
                    jobGroup.sort((a, b) => Number(b.activeFlag) - Number(a.activeFlag));
                    jobGroup.forEach(j => {
                        if (!selectedIngestModes.includes(j.ingestMode as string)) {
                            j.isSelected = true;
                            selectedIngestModes.push(j.ingestMode);
                        }
                    });
                });
                break;
            case JobPreselectionMode.FirstActive:
                jobsByEntityId.forEach(jobGroup => {
                    let firstActiveJobHasBeenSelected = false;
                    jobGroup.forEach(j => {
                        if (j.activeFlag && !firstActiveJobHasBeenSelected) {
                            j.isSelected = true;
                            firstActiveJobHasBeenSelected = true;
                        }
                    });
                });
                break;
            case JobPreselectionMode.All:
                jobs.forEach(j => (j.isSelected = true));
                break;
            case JobPreselectionMode.None:
            default:
                return;
        }
    }

    public getJobsByEntityId(jobs: SelectableJob[]): Map<number, SelectableJob[]> {
        const entityIdSet = new Set<number>(jobs.map(j => <number>j.entityId));
        const uniqueEntityIds = Array.from(entityIdSet.values());
        const map = new Map<number, SelectableJob[]>(uniqueEntityIds.map(id => [id, []]));
        jobs.forEach(j => {
            const entityJobs = <SelectableJob[]>map.get(<number>j.entityId);
            entityJobs.push(j);
        });

        return map;
    }

    private getFullEntityName(entity: EntityWithJobs): string {
        const schemaPart = entity.schemaName ? `${entity.schemaName}.` : '';
        return `${schemaPart}${entity.entityName}`;
    }
}
